import { Box, CardHeader, Typography } from '@mui/material'
import _, { get } from 'lodash'
import { Fragment, ReactElement } from 'react'
import { Feature_Config, Feature_Group_Config, Feature_Type_Config } from 'src/@types/graphql'
import { BOX_SHADOW_ON_WHITE_BG, TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { CheckboxData, CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { ListSelectionIcon } from 'src/shared/icons/Icons'
import { ExpandableCard } from 'src/shared/presentation/ExpandableCard'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'

interface Props {
  featureType: Feature_Type_Config
  featureGroups: Feature_Group_Config[]
  featureConfigs: Feature_Config[]
  featuresCheckboxData: CheckboxData[]
  showSelectionWithinGroupIcon: boolean
}

const ExpandableCardStyled = styled(ExpandableCard)`
  box-shadow: ${BOX_SHADOW_ON_WHITE_BG.default};

  &:hover {
    box-shadow: ${BOX_SHADOW_ON_WHITE_BG.hover};
  }

  .MuiCardContent-root {
    padding: 0;

    &:last-child {
      padding-bottom: 0;
    }

    .MuiFormHelperText-root {
      display: none;
    }

    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`

const CardHeaderStyled = styled(CardHeader)`
  height: 3rem;

  .MuiCardHeader-content {
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
  }
`

const TitleStyledGroup = styled(Typography)`
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  ${TYPOGRAPHY_MIXIN.subtitle2};
  font-style: normal;
`

export const ExpandableFeatureSelectionCard = ({
  featureType,
  featureGroups,
  featureConfigs,
  featuresCheckboxData,
  showSelectionWithinGroupIcon,
}: Props): ReactElement => {
  const language = useUserLocale()

  const featureConfigsByType = featureConfigs.filter(
    (featureConfig) => featureConfig.feature_type_id === featureType.id,
  )

  const getFeaturesByGroup = (features: Feature_Config[]) => {
    const featureConfigIdsArray = features.map((featureConfig) => featureConfig.id)
    return featuresCheckboxData.filter((feature) => featureConfigIdsArray.includes(feature.value as number))
  }

  const getGroupName = (features: Feature_Config[]): string | undefined => {
    const groupId = features.find((f) => f.feature_group_id)?.feature_group_id
    const group = featureGroups.find((fg) => fg.id === groupId)

    const title = get(group?.names, language, '')

    return title
  }

  const groupedFeatures = _(featureConfigsByType)
    .groupBy((feature) => feature.feature_group_id)
    .sortBy((group) => featureConfigsByType.indexOf(group[0]))
    .value()

  return (
    <ExpandableCardStyled
      header={
        <CardHeaderStyled
          title={
            <>
              <Typography variant="subtitle2">{get(featureType.names, language, '')}</Typography>
              {showSelectionWithinGroupIcon && <ListSelectionIcon color="action" />}
            </>
          }
          disableTypography
        />
      }
      content={Object.values(groupedFeatures).map((features, index) => {
        const groupName = getGroupName(features)

        if (groupName) {
          return (
            <Fragment key={index}>
              <Box mt={1}>
                <TitleStyledGroup>{groupName}</TitleStyledGroup>
                <Box ml={2} mr={2}>
                  <CheckboxGroupField name={`features.${featureType.code}`} data={getFeaturesByGroup(features)} />
                </Box>
              </Box>
            </Fragment>
          )
        } else {
          return (
            <Fragment key={index}>
              <Box mt={1}>
                <CheckboxGroupField name={`features.${featureType.code}`} data={getFeaturesByGroup(features)} />
              </Box>
            </Fragment>
          )
        }
      })}
    />
  )
}
